import { createSlice } from "@reduxjs/toolkit";
import { clearAuth } from "./authSlice";
import { ensureTabTitle } from "@/lib/tabs/registry";

export const initialState = {
  order: [],
  byId: {},
  activeId: null,
  closedTabs: {},
  tabHistory: [],
};

const pushHistoryEntry = (state, id) => {
  if (!id) return;
  if (state.tabHistory[state.tabHistory.length - 1] !== id) {
    state.tabHistory.push(id);
  }
};

const findNextActiveId = (state, closingId) => {
  for (let index = state.tabHistory.length - 1; index >= 0; index -= 1) {
    const candidateId = state.tabHistory[index];
    if (candidateId !== closingId && state.byId[candidateId]) {
      return candidateId;
    }
  }

  if (state.order.length === 0) return null;

  const closingIndex = state.order.indexOf(closingId);
  if (closingIndex > 0) return state.order[closingIndex - 1];
  return state.order[state.order.length - 1] || null;
};

const tabsSlice = createSlice({
  name: "tabs",
  initialState,
  reducers: {
    openTab: (state, action) => {
      const nextTab = ensureTabTitle(action.payload);
      if (!nextTab?.id) return;

      const { id, title, type, params } = nextTab;

      if (state.byId[id]) {
        state.byId[id] = {
          ...state.byId[id],
          title,
          type,
          params,
        };
      } else {
        state.byId[id] = { id, title, type, params };
        state.order.push(id);
      }

      delete state.closedTabs[id];
      state.activeId = id;
      pushHistoryEntry(state, id);
    },

    activateTab: (state, action) => {
      const id = action.payload;
      if (!state.byId[id]) return;
      state.activeId = id;
      pushHistoryEntry(state, id);
    },

    closeTab: (state, action) => {
      const id = action.payload;
      if (!state.byId[id]) return;

      state.closedTabs[id] = state.byId[id];
      delete state.byId[id];
      state.order = state.order.filter((t) => t !== id);

      if (state.activeId === id) {
        state.activeId = findNextActiveId(state, id);
      }
    },

    reorderTabs: (state, action) => {
      const { from, to } = action.payload;
      if (from === to) return;
      const [movedId] = state.order.splice(from, 1);
      state.order.splice(to, 0, movedId);
    },

    restoreTab: (state, action) => {
      const id = action.payload;
      if (state.byId[id]) {
        state.activeId = id;
        pushHistoryEntry(state, id);
        return;
      }

      const restoredTab = state.closedTabs[id];
      if (!restoredTab) return;

      state.byId[id] = ensureTabTitle(restoredTab);
      if (!state.order.includes(id)) {
        state.order.push(id);
      }
      state.activeId = id;
      pushHistoryEntry(state, id);
      delete state.closedTabs[id];
    },

    setTabHistory: (state, action) => {
      state.tabHistory = action.payload;
    },

    clearTabs: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(clearAuth, () => initialState);
  },
});

export const {
  openTab,
  closeTab,
  activateTab,
  reorderTabs,
  restoreTab,
  setTabHistory,
  clearTabs,
} = tabsSlice.actions;

export default tabsSlice.reducer;
